import React, { useEffect, useRef } from 'react';
import { useQuill } from 'react-quilljs';
import 'quill/dist/quill.snow.css';

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const { quill, quillRef } = useQuill({
    modules: {
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ['link', 'image'],
        ['clean'],
      ],
    },
    formats: [
      'header',
      'bold', 'italic', 'underline', 'strike',
      'list',
      'color', 'background',
      'align',
      'link', 'image'
    ],
    placeholder: placeholder,
  });

  const isInternalChange = useRef(false);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Sync value from props to Quill
  useEffect(() => {
    if (quill) {
      const currentContents = quill.root.innerHTML;
      // Only paste if the external value is different and we're not currently typing
      if (value !== undefined && value !== currentContents && !isInternalChange.current) {
        // Normalize empty values to prevent infinite loops/clearing
        const normalizedValue = value === '' ? '<p><br></p>' : value;
        if (normalizedValue !== currentContents) {
          quill.clipboard.dangerouslyPasteHTML(value || '');
        }
      }
    }
  }, [quill, value]);

  // Handle internal changes and sync back to parent
  useEffect(() => {
    if (quill) {
      const handleTextChange = () => {
        const currentContents = quill.root.innerHTML;
        // Mark as internal change so the value sync useEffect doesn't fight back
        isInternalChange.current = true;
        if (onChangeRef.current) {
          onChangeRef.current(currentContents);
        }
        
        // Reset the flag after a short delay to allow state to settle
        setTimeout(() => {
          isInternalChange.current = false;
        }, 100);
      };

      quill.on('text-change', handleTextChange);

      // Image handler
      quill.getModule('toolbar').addHandler('image', () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();
        input.onchange = async () => {
          const file = input.files[0];
          if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
              const range = quill.getSelection(true);
              quill.insertEmbed(range.index, 'image', e.target.result);
              quill.setSelection(range.index + 1);
            };
            reader.readAsDataURL(file);
          }
        };
      });

      return () => {
        quill.off('text-change', handleTextChange);
      };
    }
  }, [quill]);

  return (
    <div style={{ width: '100%', height: '200px', marginBottom: '50px' }}>
      <div ref={quillRef} />
    </div>
  );
};

export default RichTextEditor;
